package com.trevorwiebe.timesheet.admin.usermanagement.data

import com.trevorwiebe.timesheet.admin.usermanagement.domain.UserManagementRepository
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.dto.toDatabaseUserDto
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.signin.data.PasswordResetRequest
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.functions.functions
import kotlinx.datetime.LocalDate

class UserManagementRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository,
) : UserManagementRepository {

    override suspend fun createUser(
        name: String,
        email: String,
        payEntryId: String,
        nickname: String,
        hireDate: LocalDate,
        ptoBalance: Int,
        fullTime: Boolean,
        holidayTime: Boolean,
        role: UserRole,
        ownerOperatorId: String?,
    ): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            Firebase.functions
                .httpsCallable("createUser")
                .invoke(
                    CreateUserRequest(
                        name = name,
                        email = email,
                        organizationId = orgId,
                        payEntryId = payEntryId,
                        nickname = nickname,
                        hireDate = hireDate.toString(),
                        ptoBalance = ptoBalance,
                        fullTime = fullTime,
                        holidayTime = holidayTime,
                        role = role.name,
                        ownerOperatorId = ownerOperatorId,
                    )
                )
            TSResult(data = "Employee created successfully")
        }
    }

    override suspend fun updateUser(user: DatabaseUserModel): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String

            // 1. Keep the Firebase Auth email/displayName in sync.
            Firebase.functions.httpsCallable("updateUser").invoke(
                UpdateUserRequest(
                    uid = user.id,
                    email = user.email,
                    displayName = user.name,
                )
            )

            // 2. Persist the profile fields to Firestore.
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("users")
                .document(user.id)
                .set(user.toDatabaseUserDto(), merge = true)

            TSResult(data = "Employee updated successfully")
        }
    }

    override suspend fun sendPasswordResetEmail(email: String): TSResult {
        return errorWrapper {
            Firebase.functions.httpsCallable("sendPasswordResetEmail").invoke(
                PasswordResetRequest(email = email)
            )
            TSResult(data = "Password reset email sent")
        }
    }

    override suspend fun setUserDeleted(user: DatabaseUserModel, deleted: Boolean): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String

            // 1. Toggle the soft-delete flag in Firestore.
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("users")
                .document(user.id)
                .set(user.copy(deleted = deleted).toDatabaseUserDto(), merge = true)

            // 2. Block (or re-allow) login by disabling/enabling the Auth account.
            Firebase.functions.httpsCallable("setUserDisabled").invoke(
                SetUserDisabledRequest(uid = user.id, disabled = deleted)
            )

            TSResult(data = if (deleted) "Employee deleted" else "Employee restored")
        }
    }
}
